import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, ChevronLeft, ChevronRight, Layers, Target, Clock, Users, Plus, MapPin, CogIcon } from "lucide-react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { getCatalog } from "@/lib/queries/catalog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useUNEvents, useUNDayOpportunities } from "@/lib/queries/campaigns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Facebook, Twitter, Linkedin, Link, Copy } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface UNEvent {
  un_day: string;
  date: string;
  action_before: number;
  image: string;
  description: string;
}

interface UNEventsResponse {
  data: UNEvent[];
  message: string;
}

interface UNOpportunity {
  id: string;
  name: string;
  subtype: string;
  beneficiary_types: string[];
  is_active: boolean;
  status: string;
  community_need: string;
  volunteer_support: string;
  impact: string;
  relevant_un_day: string[];
  type: string;
  sdg_alignment: string[];
}

interface UNOpportunitiesResponse {
  data: UNOpportunity[];
  message: string;
}

interface CatalogEntry {
  id: string;
  opportunity_id: string;
  npo_id: string;
  title: string;
  description: string;
  prerequisites: string[];
  timeline: string[];
  event_cost: number;
  min_volunteers: number;
  max_volunteers: number;
  volunteer_output: string;
  format: string;
  duration: string;
  opportunity: {
    type: string;
    impact: string;
    sdg_alignment: string[];
    community_need: string;
    relevant_un_day: string[];
    beneficiary_types: string[];
    volunteer_support: string;
    image_url: string;
  };
  images: { image_url: string }[];
  npo: {
    city: string;
    country: string;
    org_name: string;
    logo_url: string | null;
  };
}

interface CatalogResponse {
  data: CatalogEntry[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message: string;
}

interface ShareButtonProps {
  url: string;
  title: string;
}

function ShareButtons({ url, title }: ShareButtonProps) {
  const { toast } = useToast();
  
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(`Check out volunteer opportunities ${title} on CauseConnect`)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(url);
    toast({
      title: "Link copied",
      description: "The campaign link has been copied to your clipboard",
    });
  };

  return (
    <div className="flex flex-col gap-2 min-w-[200px]">
      <p className="text-sm font-medium mb-2">Share this campaign</p>
      <Button
        variant="outline"
        size="sm"
        className="justify-start"
        onClick={() => window.open(shareLinks.twitter, '_blank')}
      >
        <Twitter className="h-4 w-4 mr-2" />
        Share on Twitter
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="justify-start"
        onClick={() => window.open(shareLinks.linkedin, '_blank')}
      >
        <Linkedin className="h-4 w-4 mr-2" />
        Share on LinkedIn
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="justify-start"
        onClick={() => window.open(shareLinks.facebook, '_blank')}
      >
        <Facebook className="h-4 w-4 mr-2" />
        Share on Facebook
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="justify-start"
        onClick={handleCopyLink}
      >
        <Copy className="h-4 w-4 mr-2" />
        Copy Link
      </Button>
    </div>
  );
}

export default function CampaignsPage({ session, profile }: { session: any; profile: any }) {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const { data: unEventsResponse, isLoading: isEventsLoading } = useUNEvents(session?.access_token, months[selectedMonth] || new Date().toLocaleString('default', { month: 'long' }));
  const unEvents = [...(unEventsResponse?.data || [])].sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const { 
    data: opportunitiesResponse, 
    isLoading: isOpportunitiesLoading,
  } = useUNDayOpportunities(session?.access_token ?? "", selectedDay);

  const { 
    data: catalogResponse, 
    isLoading: isCatalogLoading,
    error: catalogError 
  } = useQuery<CatalogResponse>({
    queryKey: ["catalog", selectedDay],
    queryFn: async () => {
      const response = await getCatalog(session?.access_token, {
        relevantUNDays: selectedDay ? [selectedDay] : undefined
      });
      if (!response) {
        throw new Error("Failed to fetch catalog");
      }
      return response;
    },
    enabled: !!session?.access_token && !!selectedDay,
  });
  
  const opportunities = opportunitiesResponse?.data || [];
  const catalog = catalogResponse?.data || [];

  // Background colors for cards
  const bgColors = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-green-500',
    'bg-rose-500',
    'bg-orange-500',
    'bg-cyan-500',
  ];

  // Helper function to format date
  const formatUNDate = (dateStr: string) => {
    const [month, day] = dateStr.split("/").map(Number);
    const date = new Date(2024, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="overflow-hidden">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-1">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">UN Campaigns</h1>
            <p className="text-muted-foreground">
              Plan your initiatives around UN International Days and create impactful opportunities.
            </p>
          </div>
          <Button onClick={() => navigate("/dashboard/catalog/create")}>
            <Layers className="mr-2 h-4 w-4" />
            Create Opportunity
          </Button>
        </div>

        {/* UN Days Timeline */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                UN International Days
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSelectedMonth(prev => (prev - 1 + 12) % 12)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="w-32 text-center font-medium">
                  {months[selectedMonth]}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSelectedMonth(prev => (prev + 1) % 12)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="w-full whitespace-nowrap">
              <div className="flex w-max space-x-4 p-4">
                {(isEventsLoading) ? (
                  // Loading skeletons
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="w-[350px] flex-shrink-0">
                      <Skeleton className="h-[200px] w-full rounded-lg" />
                    </div>
                  ))
                ) : (
                  unEvents.map((event, index) => (
                    <Card 
                      key={index} 
                      className={cn(
                        "w-[350px] flex-shrink-0 overflow-hidden group cursor-pointer transition-all",
                        selectedDay === event.un_day ? "ring-2 ring-primary" : "hover:shadow-md"
                      )}
                      onClick={() => setSelectedDay(event.un_day)}
                    >
                      <div className={cn(
                        "relative h-32",
                        bgColors[index % bgColors.length],
                        "transition-colors"
                      )}>
                        <div className="absolute bottom-4 left-4 right-4 text-white">
                          <p className="text-sm font-medium">
                            {formatUNDate(event.date)}
                          </p>
                        </div>
                      </div>
                      <CardHeader>
                        <div className="space-y-2">
                          <CardTitle className="line-clamp-2">{event.un_day}</CardTitle>
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Clock className="h-4 w-4 mr-1" />
                            <span>Plan {event.action_before} days ahead</span>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {event.description}
                        </p>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </CardContent>
        </Card>

        {/* My Catalog */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold">
              My Catalog {selectedDay && `for ${selectedDay}`}
            </h2>
            <p className="text-sm text-muted-foreground">
              Create and manage volunteer opportunities
            </p>
            {catalog.length > 0 && profile?.domains?.domain_suffix ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-4"
                  >
                    <Link className="h-4 w-4 mr-2" />
                    Share Campaign
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-3">
                  <ShareButtons 
                    url={`https://${profile?.domains?.domain_suffix}.causeconnect.ai/campaigns${selectedDay ? `/${selectedDay.toLowerCase().replace(/\s+/g, '-')}` : ''}`} 
                    title={selectedDay ? `for ${selectedDay}` : 'My Volunteer Opportunities'} 
                  />
                </PopoverContent>
              </Popover>
            ) : catalog.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                className="ml-4"
                onClick={() => navigate('/dashboard/mission-hub/setup')}
              >
                <CogIcon className="h-4 w-4 mr-2" />
                Setup Mission Hub to Start Sharing
              </Button>
            )}
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isCatalogLoading ? (
              // Loading skeletons for catalog
              Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-[200px] w-full rounded-lg" />
              ))
            ) : catalog.length > 0 ? (
              catalog.map((entry) => (
                <Card 
                  key={entry.id} 
                  className="overflow-hidden group cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/dashboard/catalog/${entry.id}`)}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={entry.opportunity.image_url || entry.images[0]?.image_url || "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b"}
                      alt={entry.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <Badge variant="secondary" className="mb-2">
                        {entry.opportunity.type}
                      </Badge>
                      <h3 className="text-lg font-semibold text-white line-clamp-2">
                        {entry.title}
                      </h3>
                    </div>
                  </div>
                  <CardContent className="p-6 space-y-4">
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {entry.description}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4 mr-2" />
                        <span>{entry.format}</span>
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Clock className="h-4 w-4 mr-2" />
                        <span>{entry.duration}</span>
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Users className="h-4 w-4 mr-2" />
                        <span>{entry.min_volunteers}-{entry.max_volunteers} volunteers</span>
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span>{entry.opportunity.relevant_un_day[0]}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center py-8">
                <p className="text-muted-foreground">
                  {selectedDay 
                    ? `No catalog entries found for ${selectedDay}. Create one from the Opportunities below.` 
                    : "Select a UN International Day to view related catalog entries"}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Opportunities Grid */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Target className="h-5 w-5" />
              {selectedDay ? `Opportunities for ${selectedDay}` : 'Related Opportunities'}
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isOpportunitiesLoading ? (
              // Loading skeletons for opportunities
              Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-[200px] w-full rounded-lg" />
              ))
            ) : opportunities.length > 0 ? (
              opportunities.map((opportunity) => (
                <Card key={opportunity.id} className="group cursor-pointer hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="space-y-1">
                        <h3 className="font-semibold">{opportunity.name}</h3>
                        <div className="flex items-center text-sm text-muted-foreground">
                          <span>{opportunity.type}</span>
                          <span className="mx-2">•</span>
                          <span className={cn(
                            "capitalize",
                            opportunity.is_active ? "text-green-600" : "text-orange-600"
                          )}>
                            {opportunity.status}
                          </span>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/dashboard/catalog/create', {
                            state: {
                              preselectedOpportunity: {
                                ...opportunity,
                                un_days: opportunity.relevant_un_day
                              }
                            }
                          });
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      {opportunity.community_need}
                    </p>
                    {opportunity.volunteer_support && (
                      <div className="flex items-center text-sm text-muted-foreground mt-2">
                        <Users className="h-4 w-4 mr-2" />
                        <span className="line-clamp-1">{opportunity.volunteer_support}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : selectedDay ? (
              <p className="text-muted-foreground col-span-full text-center py-8">
                No opportunities found for {selectedDay}. Create one to get started!
              </p>
            ) : (
              <p className="text-muted-foreground col-span-full text-center py-8">
                Select a UN International Day to view related opportunities
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
